import { MIN_ZOOM } from './worldmap-renderer';

/**
 * Martian cloud overlay for the world map — the motion model, kept pure (no
 * canvas, no DOM, no clock of its own) so it unit-tests in Node and the
 * screen only has to feed it `now` and the current camera.
 *
 * Clouds are anchored to the MAP, not the screen: each one lives at a
 * fractional position over the map's own rectangle (a wrapping "torus" a
 * little larger than the map, so they drift off one edge before re-entering
 * on the other). Panning therefore carries them along with the terrain, and
 * zooming scales their spacing with it, exactly like any other map feature.
 *
 * On top of that sit two small camera-dependent effects:
 *  - PARALLAX: the layer is "high above" the ground, so it shifts a hair more
 *    than the terrain when you pan (factor slightly above 1, measured from the
 *    pan at which the map is centred on the screen — zero shift there).
 *  - ZOOM PUSH: the closer you zoom, the further each cloud is pushed out from
 *    the viewport centre and the fainter the layer gets, so the player's view
 *    of the ground clears as they come in.
 *
 * Positions are a pure function of time (`start + velocity * t`), never
 * accumulated per frame — same lazy-evaluation rule as the rest of the game,
 * and a tab that was hidden for a minute resumes in the right place.
 */

/**
 * The map is ~6 viewports wide and ~5 tall at default zoom, so only ~1/30 of the
 * sky is on screen at once: this many clouds puts a handful in view. Off-screen
 * ones cost one cheap placement calculation and are culled before drawing.
 */
export const CLOUD_COUNT = 140;
/** Distinct sprite images the layer expects (see `loadCloudImage` in worldmap-screen.ts). */
export const CLOUD_SPRITE_COUNT = 4;

/** How far past the map edge (as a fraction of the map's size) the wrap range extends on each side. */
const WRAP_MARGIN = 0.06;
/** Fraction of the wrap range, at each end, over which a cloud fades in/out so the re-entry is never a visible pop. */
const EDGE_FADE_BAND = 0.03;
/**
 * Parallax: clouds follow a pan at this multiple of the terrain's own pan.
 * 1 = glued to the ground; the extra 0.06 is a deliberately subtle depth cue.
 */
export const CLOUD_PARALLAX = 1.06;
/** At max zoom a cloud's offset from the viewport centre is multiplied by (1 + PUSH). Kept small: the map's own zoom already spreads them. */
const PUSH = 0.5;
/** At max zoom the layer is this much fainter overall. */
const ZOOM_FADE = 0.45;

export interface CloudSpec {
  /** Index into the loaded sprite list. */
  sprite: number;
  /** Start position in [0,1) of the wrap range, per axis. */
  u: number;
  v: number;
  /** Drift in wrap-range fractions per second. */
  vx: number;
  vy: number;
  /** Width as a fraction of the MAP's width (so clouds scale with zoom like terrain). */
  size: number;
  /** Peak opacity, 0..1 — different per cloud, the "variable transparency". */
  alpha: number;
  /** Phase (rad) and rate (rad/s) of the slow opacity breathing. */
  phase: number;
  pulse: number;
}

/** The camera, in screen CSS px. One instance is reused every frame. */
export interface CloudView {
  zoom: number;
  maxZoom: number;
  viewportWidth: number;
  viewportHeight: number;
  /** Top-left of the map's rectangle on screen (the pan offset) and its size at the current zoom. */
  mapX: number;
  mapY: number;
  mapWidth: number;
  mapHeight: number;
}

export interface CloudPlacement {
  /** Centre of the sprite, in viewport CSS px. */
  x: number;
  y: number;
  /** Drawn width in CSS px; height follows the sprite's own aspect. */
  width: number;
  alpha: number;
}

export function emptyView(): CloudView {
  return { zoom: 1, maxZoom: 1, viewportWidth: 0, viewportHeight: 0, mapX: 0, mapY: 0, mapWidth: 0, mapHeight: 0 };
}

export function emptyPlacement(): CloudPlacement {
  return { x: 0, y: 0, width: 0, alpha: 0 };
}

/** Small deterministic PRNG so the sky is the same every visit (and testable). */
function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function buildClouds(count: number = CLOUD_COUNT, spriteCount: number = CLOUD_SPRITE_COUNT, seed = 20261012): CloudSpec[] {
  const rand = mulberry32(seed);
  const clouds: CloudSpec[] = [];
  for (let i = 0; i < count; i++) {
    // Wind blows broadly one way (left-to-right, slightly downward) with per-cloud
    // variation in speed and a little cross-drift, so the sky reads as one weather
    // system rather than random confetti. Speeds are map fractions per second: the
    // map is ~6 viewports wide at default zoom, so ~0.0012–0.003 is a slow crawl
    // and a full crossing takes several minutes.
    const speed = 0.0012 + rand() * 0.0018;
    clouds.push({
      sprite: i % Math.max(1, spriteCount),
      u: rand(),
      v: rand(),
      vx: speed,
      vy: (rand() - 0.35) * 0.0006,
      size: 0.065 + rand() * 0.085,
      alpha: 0.45 + rand() * 0.5,
      phase: rand() * Math.PI * 2,
      pulse: 0.15 + rand() * 0.25,
    });
  }
  return clouds;
}

/** 0 at MIN_ZOOM (fully zoomed out) .. 1 at the viewport's own max zoom. Clamped. */
export function zoomProgress(zoom: number, maxZoom: number): number {
  const span = maxZoom - MIN_ZOOM;
  if (!(span > 0)) return 0;
  return Math.min(1, Math.max(0, (zoom - MIN_ZOOM) / span));
}

/** Positive modulo — JS `%` keeps the dividend's sign. */
function wrap(value: number, span: number): number {
  return ((value % span) + span) % span;
}

function smoothstep(x: number): number {
  const c = Math.min(1, Math.max(0, x));
  return c * c * (3 - 2 * c);
}

/**
 * Where (and how opaque) one cloud is at `nowMs`, written into `out` — callers
 * keep one preallocated CloudPlacement per cloud so the per-frame loop makes no
 * garbage.
 */
export function placeCloud(spec: CloudSpec, nowMs: number, view: CloudView, out: CloudPlacement): CloudPlacement {
  const t = zoomProgress(view.zoom, view.maxZoom);
  const seconds = nowMs / 1000;
  const range = 1 + 2 * WRAP_MARGIN;

  // Position inside the wrap range, then mapped to a fraction of the MAP
  // (range starts WRAP_MARGIN off the map's left/top edge).
  const ru = wrap(spec.u + spec.vx * seconds, 1);
  const rv = wrap(spec.v + spec.vy * seconds, 1);
  const fu = -WRAP_MARGIN + ru * range;
  const fv = -WRAP_MARGIN + rv * range;

  // Glued to the ground: the screen position the terrain at that map fraction has.
  let x = view.mapX + fu * view.mapWidth;
  let y = view.mapY + fv * view.mapHeight;

  // Parallax: extra shift proportional to how far the camera is from "map centred".
  const parallax = CLOUD_PARALLAX - 1;
  x += parallax * (view.mapX - (view.viewportWidth - view.mapWidth) / 2);
  y += parallax * (view.mapY - (view.viewportHeight - view.mapHeight) / 2);

  // Zoom push: spread outward from the map's centre as the camera comes in. Taken
  // from the cloud's offset to the MAP centre (not the screen centre) so it depends
  // only on zoom — panning stays 1:1 with the ground instead of being multiplied.
  const pushExtra = PUSH * t;
  out.x = x + (fu - 0.5) * view.mapWidth * pushExtra;
  out.y = y + (fv - 0.5) * view.mapHeight * pushExtra;
  out.width = spec.size * view.mapWidth;

  // Fade in/out at the ends of the wrap range so the re-entry never pops.
  const edge = Math.min(ru, 1 - ru, rv, 1 - rv);
  const edgeFade = smoothstep(edge / EDGE_FADE_BAND);
  const breathing = 0.7 + 0.3 * Math.sin(spec.phase + seconds * spec.pulse);
  out.alpha = spec.alpha * breathing * edgeFade * (1 - ZOOM_FADE * t);
  return out;
}
